/**
 * shipCombat.ts
 * Pool, cooldown, fire, and update for the Courier Ship's primary weapon.
 *
 * Mirrors `projectileSystem.ts` (Nova's Star Shards) but tailored to the
 * space shmup:
 *   - Single-shot (no burst).
 *   - Always forward (no facing — the ship faces right).
 *   - Spawns from the ship's nose, vertically centred on the hitbox.
 *   - Fire while `attackHeld` is true and cooldown is elapsed (gives the
 *     player a continuous stream when holding the button — standard shmup
 *     convention rather than the platformer's edge-triggered burst).
 *
 * Pool is pre-warmed at module load. `resetShipCombat()` returns every active
 * projectile to the pool and zeroes the cooldown so a level restart is clean.
 *
 * NO React imports, NO Skia imports, NO async.
 *
 * Phase 4 — World 2: Starfield Drift.
 *
 * @module shipCombat
 */

import { spaceState } from './spaceState';
import { inputState } from '../input/inputState';
import { ShipProjectile } from '../entities/ShipProjectile';
import { ShipMissile } from '../entities/ShipMissile';
import { ObjectPool } from '../utils/objectPool';
import type { LevelRuntime } from './gameState';
import {
  SHIP_MAX_PROJECTILES,
  SHIP_PROJECTILE_COOLDOWN_S,
  SHIP_PROJECTILE_HEIGHT,
  SHIP_MAX_MISSILES,
  SHIP_MISSILE_COOLDOWN_S,
  SHIP_MISSILE_HEIGHT,
  SHIP_MISSILE_HOLD_THRESHOLD_S,
} from '../../constants/spaceConstants';

/**
 * Module-level ship-projectile pool — pre-warmed at module load.
 * Shared across all physics ticks (one game loop at a time).
 */
const shipProjectilePool = new ObjectPool<ShipProjectile>(
  () => new ShipProjectile(),
  SHIP_MAX_PROJECTILES,
);
shipProjectilePool.prewarm(SHIP_MAX_PROJECTILES);

/**
 * Module-level ship-missile pool — pre-warmed at module load.
 * Smaller cap than the laser pool; cooldown + one-shot-per-hold keeps
 * the steady-state in-flight count at 1–2.
 */
const shipMissilePool = new ObjectPool<ShipMissile>(
  () => new ShipMissile(),
  SHIP_MAX_MISSILES,
);
shipMissilePool.prewarm(SHIP_MAX_MISSILES);

/**
 * Return every active ship projectile + missile to the pool and reset
 * both cooldowns plus the Attack-hold tracking. Call from
 * `SpaceScene.init()` and `SpaceScene.destroy()` so combat state never
 * bleeds across sessions.
 */
export function resetShipCombat(): void {
  for (const p of spaceState.projectiles) {
    shipProjectilePool.release(p);
  }
  spaceState.projectiles = [];
  spaceState.attackCooldown = 0;
  for (const m of spaceState.missiles) {
    shipMissilePool.release(m);
  }
  spaceState.missiles = [];
  spaceState.missileCooldown = 0;
  spaceState.attackHoldDuration = 0;
  spaceState.missileFiredThisHold = false;
}

/**
 * Decrement the attack cooldown by one tick.
 * Clamped at 0 to prevent FP drift from accumulating below zero.
 *
 * @param dt - Fixed timestep in seconds.
 */
export function updateShipCooldown(dt: number): void {
  if (spaceState.attackCooldown > 0) {
    spaceState.attackCooldown = Math.max(0, spaceState.attackCooldown - dt);
  }
}

/**
 * Fire one projectile if the ship is alive, the player is holding Attack, and
 * the cooldown is elapsed. No-op otherwise.
 *
 * DECISION: Uses `attackHeld` (continuous stream while held) rather than
 * `attackPressed` (one shot per press). Shmup convention; also avoids
 * burning the rising-edge flag on the very first frame of a level when input
 * may not have settled.
 *
 * DECISION: When the pool is exhausted (all 20 projectiles in flight) the
 * shot is silently dropped and the cooldown does NOT advance — mirrors the
 * Star Shard behaviour from `tryFireProjectiles` so a dead press doesn't
 * lock the player out of the next frame's shot.
 */
export function tryFireShipProjectile(): void {
  const ship = spaceState.ship;
  if (ship === null || ship.dead) return;
  if (!inputState.attackHeld) return;
  if (spaceState.attackCooldown > 0) return;

  const projectile = shipProjectilePool.acquire();
  if (projectile === null) return;

  // Spawn at the ship's nose (right edge), vertically centred on the hitbox.
  const spawnX = ship.x + ship.width;
  const spawnY = ship.y + ship.height / 2 - SHIP_PROJECTILE_HEIGHT / 2;
  projectile.spawn(spawnX, spawnY);
  spaceState.projectiles.push(projectile);
  spaceState.attackCooldown = SHIP_PROJECTILE_COOLDOWN_S;
}

/** Reusable empty level handed to projectile updates — space has no platforms. */
const EMPTY_SPACE_LEVEL: LevelRuntime = { width: 0, height: 0, platforms: [] };

/**
 * Update every active ship projectile and release dead ones back to the pool.
 *
 * @param dt - Fixed timestep in seconds.
 */
export function updateShipProjectiles(dt: number): void {
  for (let i = spaceState.projectiles.length - 1; i >= 0; i--) {
    const p = spaceState.projectiles[i];
    p.update(dt, EMPTY_SPACE_LEVEL);
    if (p.dead) {
      shipProjectilePool.release(p);
      spaceState.projectiles.splice(i, 1);
    }
  }
}

/**
 * Decrement the missile cooldown by one tick. Clamped at 0.
 *
 * @param dt - Fixed timestep in seconds.
 */
export function updateMissileCooldown(dt: number): void {
  if (spaceState.missileCooldown > 0) {
    spaceState.missileCooldown = Math.max(0, spaceState.missileCooldown - dt);
  }
}

/**
 * Track how long the Attack input has been held continuously.
 *
 * - While `attackHeld` is true: accumulate `dt` into
 *   `spaceState.attackHoldDuration`.
 * - When the player releases Attack: reset the duration to 0 AND clear
 *   `missileFiredThisHold` so the next hold gesture can fire a fresh
 *   missile after crossing the threshold again.
 *
 * Called every tick from `stepSpace` before `tryFireShipMissile`.
 *
 * @param dt - Fixed timestep in seconds.
 */
export function updateAttackHold(dt: number): void {
  if (inputState.attackHeld) {
    spaceState.attackHoldDuration += dt;
  } else {
    spaceState.attackHoldDuration = 0;
    spaceState.missileFiredThisHold = false;
  }
}

/**
 * Fire one missile if the player has held Attack past the threshold,
 * has not already fired during this hold, and the cooldown is elapsed.
 *
 * DECISION: One missile per Attack-hold gesture. The player must
 * release Attack and re-hold past the threshold to fire another. This
 * keeps the secondary weapon a deliberate input rather than a constant
 * stream once the player rests their finger on Attack.
 *
 * DECISION: Pool exhaustion silently drops the shot and does NOT
 * advance the cooldown, mirroring `tryFireShipProjectile`. The hold
 * flag still flips so a sustained hold can't spin the pool by repeated
 * acquire/release attempts on the same tick.
 */
export function tryFireShipMissile(): void {
  const ship = spaceState.ship;
  if (ship === null || ship.dead) return;
  if (!inputState.attackHeld) return;
  if (spaceState.missileFiredThisHold) return;
  if (spaceState.attackHoldDuration < SHIP_MISSILE_HOLD_THRESHOLD_S) return;
  if (spaceState.missileCooldown > 0) return;

  const missile = shipMissilePool.acquire();
  if (missile === null) {
    // Pool empty — flip the hold flag so we don't burn an acquire attempt
    // every tick until the player releases. Cooldown stays at zero so the
    // next free pool slot can fire immediately.
    spaceState.missileFiredThisHold = true;
    return;
  }

  const spawnX = ship.x + ship.width;
  const spawnY = ship.y + ship.height / 2 - SHIP_MISSILE_HEIGHT / 2;
  missile.spawn(spawnX, spawnY);
  spaceState.missiles.push(missile);
  spaceState.missileCooldown = SHIP_MISSILE_COOLDOWN_S;
  spaceState.missileFiredThisHold = true;
}

/**
 * Update every active ship missile and release dead ones back to the
 * pool. Mirrors {@link updateShipProjectiles}.
 *
 * @param dt - Fixed timestep in seconds.
 */
export function updateShipMissiles(dt: number): void {
  for (let i = spaceState.missiles.length - 1; i >= 0; i--) {
    const m = spaceState.missiles[i];
    m.update(dt, EMPTY_SPACE_LEVEL);
    if (m.dead) {
      shipMissilePool.release(m);
      spaceState.missiles.splice(i, 1);
    }
  }
}

